import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type {
  PrizeDelivery,
  DeliveryStats,
  DeliveryFilters,
  UserPrizeTracking,
  DeliveryStatus,
  ShippingCompany
} from '../types/prizeDelivery'
import type { ShippingAddress } from '../types/user'

interface PrizeDeliveryState {
  // Current data
  deliveries: PrizeDelivery[]
  userDeliveries: UserPrizeTracking[]
  stats: DeliveryStats | null

  // UI state
  isLoading: boolean
  error: string | null
  selectedDelivery: PrizeDelivery | null
  filters: DeliveryFilters

  // Pagination
  currentPage: number
  totalPages: number
  itemsPerPage: number

  // Actions
  loadUserDeliveries: (userId: string) => Promise<void>
  loadAdminDeliveries: (filters?: DeliveryFilters) => Promise<void>
  loadDeliveryStats: () => Promise<void>

  // Delivery management
  createPrizeDelivery: (params: CreatePrizeDeliveryParams) => Promise<string>
  updateDeliveryStatus: (deliveryItemId: string, status: DeliveryStatus, notes?: string) => Promise<void>
  updateTrackingInfo: (deliveryItemId: string, trackingData: UpdateTrackingParams) => Promise<void>
  updateShippingAddress: (prizeDeliveryId: string, address: ShippingAddress) => Promise<void>

  // Bulk operations for admin
  bulkUpdateStatus: (deliveryItemIds: string[], status: DeliveryStatus, notes?: string) => Promise<void>
  assignAdmin: (prizeDeliveryIds: string[], adminUserId: string) => Promise<void>

  // Filters and search
  setFilters: (filters: Partial<DeliveryFilters>) => void
  clearFilters: () => void
  searchDeliveries: (query: string) => void

  // UI helpers
  setSelectedDelivery: (delivery: PrizeDelivery | null) => void
  setPage: (page: number) => void
  clearError: () => void

  // Real-time subscriptions
  subscribeToUserDeliveries: (userId: string) => () => void
  subscribeToAdminDeliveries: (filters?: DeliveryFilters) => () => void

  // Utilities
  getDeliveryProgress: (prizeDeliveryId: string) => number
  getOverdueDeliveries: () => PrizeDelivery[]
  getDeliveriesByStatus: (status: DeliveryStatus) => PrizeDelivery[]
}

interface CreatePrizeDeliveryParams {
  raffleId: string
  userId: string
  winnerId: string
  ticketId: string
  winningNumber: string
  prizeType: 'main' | 'golden' | 'bonus' | 'feeding-jackpot'
  modalityType: string
  totalPrizeValue: number
  deliveryConfig: {
    frequency: 'single' | 'daily' | 'weekly' | 'bi-weekly' | 'monthly'
    count: number
    startDate?: Date
    items: Array<{
      title: string
      description: string
      estimatedValue: number
      items: string[]
      scheduledDate?: Date
    }>
  }
  shippingAddress: ShippingAddress
}

interface UpdateTrackingParams {
  trackingNumber?: string
  trackingUrl?: string
  shippingCompany?: ShippingCompany
  estimatedDeliveryDate?: Date
  publicNotes?: string
}

// Default filters
const defaultFilters: DeliveryFilters = {
  status: undefined,
  modality: undefined,
  priority: undefined,
  shippingCompany: undefined,
  assignedAdmin: undefined,
  overdue: undefined,
  dateRange: undefined,
  search: undefined
}


export const usePrizeDeliveryStore = create<PrizeDeliveryState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    deliveries: [],
    userDeliveries: [],
    stats: null,
    isLoading: false,
    error: null,
    selectedDelivery: null,
    filters: defaultFilters,
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 20,

    // Load user's prize deliveries
    loadUserDeliveries: async (userId: string) => {
      set({ isLoading: true, error: null })

      try {
        // Import the service dynamically to avoid circular dependencies
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        const userDeliveries = await prizeDeliveryService.getUserDeliveries(userId)

        set({
          userDeliveries,
          isLoading: false
        })
      } catch (error) {
        console.error('Error loading user deliveries:', error)
        set({
          error: error instanceof Error ? error.message : 'Error cargando entregas',
          isLoading: false
        })
      }
    },

    // Load deliveries for admin with filters
    loadAdminDeliveries: async (filters?: DeliveryFilters) => {
      set({ isLoading: true, error: null })

      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        const currentFilters = filters || get().filters
        const currentPage = get().currentPage
        const itemsPerPage = get().itemsPerPage

        const result = await prizeDeliveryService.getAdminDeliveries({
          ...currentFilters,
          page: currentPage,
          limit: itemsPerPage
        })

        set({
          deliveries: result.deliveries,
          totalPages: Math.ceil(result.total / itemsPerPage),
          isLoading: false
        })
      } catch (error) {
        console.error('Error loading admin deliveries:', error)
        set({
          error: error instanceof Error ? error.message : 'Error cargando entregas',
          isLoading: false
        })
      }
    },

    // Load delivery statistics
    loadDeliveryStats: async () => {
      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        const stats = await prizeDeliveryService.getDeliveryStats()
        set({ stats })
      } catch (error) {
        console.error('Error loading delivery stats:', error)
        // Don't set error for stats loading failure
      }
    },

    // Create new prize delivery
    createPrizeDelivery: async (params: CreatePrizeDeliveryParams) => {
      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        const prizeDeliveryId = await prizeDeliveryService.createPrizeDelivery(params)

        // Reload data
        await get().loadAdminDeliveries()
        await get().loadDeliveryStats()

        return prizeDeliveryId
      } catch (error) {
        console.error('Error creating prize delivery:', error)
        set({ error: error instanceof Error ? error.message : 'Error creando entrega' })
        throw error
      }
    },

    // Update delivery status
    updateDeliveryStatus: async (deliveryItemId: string, status: DeliveryStatus, notes?: string) => {
      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        await prizeDeliveryService.updateDeliveryStatus(deliveryItemId, status, notes)

        // Reload data
        await get().loadAdminDeliveries()
        await get().loadDeliveryStats()
      } catch (error) {
        console.error('Error updating delivery status:', error)
        set({ error: error instanceof Error ? error.message : 'Error actualizando estado' })
        throw error
      }
    },

    // Update tracking information
    updateTrackingInfo: async (deliveryItemId: string, trackingData: UpdateTrackingParams) => {
      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        await prizeDeliveryService.updateTrackingInfo(deliveryItemId, trackingData)

        // Reload data
        await get().loadAdminDeliveries()
      } catch (error) {
        console.error('Error updating tracking info:', error)
        set({ error: error instanceof Error ? error.message : 'Error actualizando seguimiento' })
        throw error
      }
    },

    // Update shipping address
    updateShippingAddress: async (prizeDeliveryId: string, address: ShippingAddress) => {
      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        await prizeDeliveryService.updateShippingAddress(prizeDeliveryId, address)

        // Reload data
        await get().loadAdminDeliveries()
      } catch (error) {
        console.error('Error updating shipping address:', error)
        set({ error: error instanceof Error ? error.message : 'Error actualizando dirección' })
        throw error
      }
    },

    // Bulk update status
    bulkUpdateStatus: async (deliveryItemIds: string[], status: DeliveryStatus, notes?: string) => {
      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        await prizeDeliveryService.bulkUpdateStatus(deliveryItemIds, status, notes)

        // Reload data
        await get().loadAdminDeliveries()
        await get().loadDeliveryStats()
      } catch (error) {
        console.error('Error bulk updating status:', error)
        set({ error: error instanceof Error ? error.message : 'Error actualizando estados' })
        throw error
      }
    },

    // Assign admin to deliveries
    assignAdmin: async (prizeDeliveryIds: string[], adminUserId: string) => {
      try {
        const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
        await prizeDeliveryService.assignAdmin(prizeDeliveryIds, adminUserId)

        // Reload data
        await get().loadAdminDeliveries()
      } catch (error) {
        console.error('Error assigning admin:', error)
        set({ error: error instanceof Error ? error.message : 'Error asignando administrador' })
        throw error
      }
    },

    // Filter management
    setFilters: (newFilters: Partial<DeliveryFilters>) => {
      const currentFilters = get().filters
      const updatedFilters = { ...currentFilters, ...newFilters }

      set({
        filters: updatedFilters,
        currentPage: 1 // Reset to first page when filters change
      })

      // Automatically reload with new filters
      get().loadAdminDeliveries(updatedFilters)
    },

    clearFilters: () => {
      set({
        filters: defaultFilters,
        currentPage: 1
      })
      get().loadAdminDeliveries(defaultFilters)
    },

    searchDeliveries: (query: string) => {
      get().setFilters({ search: query || undefined })
    },

    // UI state management
    setSelectedDelivery: (delivery: PrizeDelivery | null) => {
      set({ selectedDelivery: delivery })
    },

    setPage: (page: number) => {
      set({ currentPage: page })
      get().loadAdminDeliveries()
    },

    clearError: () => {
      set({ error: null })
    },

    // Real-time subscriptions
    subscribeToUserDeliveries: (userId: string) => {
      let unsubscribe: (() => void) | null = null;

      (async () => {
        try {
          const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
          unsubscribe = prizeDeliveryService.subscribeToUserDeliveries(userId, (userDeliveries) => {
            set({ userDeliveries })
          })
        } catch (error) {
          console.error('Error subscribing to user deliveries:', error)
        }
      })()

      return () => {
        if (unsubscribe) {
          unsubscribe()
        }
      }
    },

    subscribeToAdminDeliveries: (filters?: DeliveryFilters) => {
      let unsubscribe: (() => void) | null = null;

      (async () => {
        try {
          const { prizeDeliveryService } = await import('../services/prizeDeliveryService')
          const currentFilters = filters || get().filters

          unsubscribe = prizeDeliveryService.subscribeToAdminDeliveries(currentFilters, (deliveries) => {
            set({ deliveries })
          })
        } catch (error) {
          console.error('Error subscribing to admin deliveries:', error)
        }
      })()

      return () => {
        if (unsubscribe) {
          unsubscribe()
        }
      }
    },

    // Utility functions
    getDeliveryProgress: (prizeDeliveryId: string) => {
      const delivery = get().deliveries.find(d => d.id === prizeDeliveryId)
      if (!delivery) return 0

      return delivery.progressPercentage
    },

    getOverdueDeliveries: () => {
      return get().deliveries.filter(delivery => delivery.isOverdue)
    },

    getDeliveriesByStatus: (status: DeliveryStatus) => {
      return get().deliveries.filter(delivery =>
        delivery.deliveries.some(item => item.status === status)
      )
    }
  }))
)

// Selectors for computed values
export const prizeDeliverySelectors = {
  // Get deliveries for current user
  getUserDeliveries: (state: PrizeDeliveryState) => state.userDeliveries,

  // Get pending deliveries count
  getPendingCount: (state: PrizeDeliveryState) =>
    state.stats?.byStatus.pending || 0,

  // Get overdue deliveries count
  getOverdueCount: (state: PrizeDeliveryState) =>
    state.stats?.overdue || 0,

  // Check if user has active deliveries
  hasActiveDeliveries: (state: PrizeDeliveryState) =>
    state.userDeliveries.some(d =>
      d.overallStatus === 'pending' || d.overallStatus === 'in-progress'
    ),

  // Get recent delivery activity
  getRecentActivity: (state: PrizeDeliveryState) => {
    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

    return state.deliveries.filter(delivery =>
      delivery.updatedAt >= sevenDaysAgo
    ).slice(0, 10)
  }
}